/**
 * Guest přesun rezervace — hash route #preplanovat?id=…&t=…
 * Bod 7 ze schůzky 24.6.2026.
 */
import { useEffect, useMemo, useState } from 'react'
import { apiUrl } from '../lib/api'

const CZ_MONTHS = ['Leden','Únor','Březen','Duben','Květen','Červen','Červenec','Srpen','Září','Říjen','Listopad','Prosinec']
const CZ_DAYS = ['Po','Út','St','Čt','Pá','So','Ne']

// Hodinové sloty 9–21 — musí souhlasit s HOUR_SLOTS v ReservationFlow
// a s backendem, který přesun počítá vždy na 1 hodinu.
const SPORT_SLOTS = [
  '09:00','10:00','11:00','12:00','13:00','14:00',
  '15:00','16:00','17:00','18:00','19:00','20:00',
]

/** Odpověď /day-grid — kurty a jejich obsazené úseky pro daný den. */
type GridResource = {
  id: number
  code: string
  label: string
  booked: { start: string; end: string }[]
}

type RescheduleInfo = {
  id: number
  reservation_type: string
  from_dt: string
  to_dt: string
  resource_id: number | null
  resource_label: string | null
  price_czk: number | null
  /** 'hourly' = sport/sauna (vybírá se čas), 'stay' = pobyt (rozsah dnů) */
  kind: 'hourly' | 'stay'
  slot_starts: string[]
  lead_text: string
  can_reschedule: boolean
  reason: string | null
}

export function Reschedule() {
  // Parse hash params: #preplanovat?id=123&t=abc
  const params = useMemo(() => {
    const q = window.location.hash.split('?')[1] || ''
    return new URLSearchParams(q)
  }, [])
  const reservationId = params.get('id')
  const token = params.get('t') || ''

  const today = new Date()
  const [viewYear, setViewYear] = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth())
  const [selDate, setSelDate] = useState<string | null>(null)
  const [selTo, setSelTo] = useState<string | null>(null)     // pobyt: odjezd
  const [selHour, setSelHour] = useState<string | null>(null)
  const [grid, setGrid] = useState<GridResource[]>([])
  const [info, setInfo] = useState<RescheduleInfo | null>(null)
  const [infoErr, setInfoErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState<{vs?: string; from_dt?: string} | null>(null)

  const days = useMemo(() => buildMonth(viewYear, viewMonth), [viewYear, viewMonth])

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1) }
    else setViewMonth(viewMonth - 1)
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1) }
    else setViewMonth(viewMonth + 1)
  }

  // Co se vlastně přesouvá — bez toho stránka hádala typ (brala tenis i pro beach)
  useEffect(() => {
    if (!reservationId || !token) return
    fetch(apiUrl(`/api/sobacov/reservations/${reservationId}/reschedule-info?token=${encodeURIComponent(token)}`))
      .then(async (r) => {
        const j = await r.json()
        if (!r.ok) throw new Error(j.detail || 'Rezervaci nelze načíst')
        return j as RescheduleInfo
      })
      .then(setInfo)
      .catch((e) => setInfoErr(e.message))
  }, [reservationId, token])

  // Obsazenost vybraného dne pro SKUTEČNÝ typ rezervace
  useEffect(() => {
    if (!selDate || !info) return
    setLoading(true)
    fetch(apiUrl(`/api/sobacov/day-grid?date=${selDate}&resource_type=${info.reservation_type}`))
      .then((r) => (r.ok ? r.json() : { resources: [] }))
      .then((j) => setGrid(j.resources || []))
      .catch(() => setGrid([]))
      .finally(() => setLoading(false))
  }, [selDate, info?.reservation_type])

  /**
   * Je hodina obsazená? Pokud rezervace drží konkrétní kurt, rozhoduje jen
   * ten; jinak musí být obsazené všechny kurty daného typu.
   */
  const isStay = info?.kind === 'stay'
  /** Sloty posílá backend (sport po hodině, sauna 15:00 a 17:30). */
  const slots = info?.slot_starts?.length ? info.slot_starts : SPORT_SLOTS

  function hourTaken(h: string): boolean {
    if (!grid.length) return false
    const covers = (r: GridResource) =>
      r.booked.some((b) => b.start <= h && h < (b.end === '00:00' ? '24:00' : b.end))
    const mine = info?.resource_id ? grid.find((r) => r.id === info.resource_id) : null
    return mine ? covers(mine) : grid.every(covers)
  }

  async function submit() {
    if (!reservationId || !token || !selDate) return
    if (isStay ? !selTo : !selHour) return
    setBusy(true)
    setErr(null)
    try {
      const r = await fetch(apiUrl(`/api/sobacov/reservations/${reservationId}/reschedule`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          isStay
            ? { token, new_from_dt: selDate, new_to_dt: selTo }
            : { token, new_from_dt: selDate, new_hour_start: selHour },
        ),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.detail || 'Nepodařilo se přesunout')
      setDone({ vs: j.variable_symbol, from_dt: j.from_dt })
    } catch (e: any) {
      setErr(e.message)
    } finally {
      setBusy(false)
    }
  }

  if (!reservationId || !token) {
    return (
      <section className="bg-gradient-to-b from-white to-steel-50/40 py-20">
        <div className="container-x max-w-xl text-center">
          <div className="mx-auto w-20 h-20 rounded-full bg-brand-50 border border-brand-200 grid place-items-center mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="h-display text-3xl">Neplatný odkaz</h1>
          <p className="text-ink-700 mt-3">Použijte prosím odkaz z potvrzovací SMS nebo e‑mailu.</p>
          <a href="#" className="btn-book mt-6 inline-flex">Zpět na úvod</a>
        </div>
      </section>
    )
  }

  if (done) {
    return (
      <section className="bg-gradient-to-b from-white to-steel-50/40 py-20">
        <div className="container-x max-w-xl">
          <div className="text-center">
            <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-forest-50 to-forest-100 border border-forest-200 grid place-items-center mb-4 shadow-[0_8px_30px_-8px_rgba(15,74,56,0.4)]">
              <span className="text-4xl text-forest-700 font-bold">✓</span>
            </div>
            <h1 className="h-display text-3xl">Termín přesunut</h1>
            <p className="text-ink-700 mt-3">
              Nový termín: <b className="text-ink-900">{czDate(done.from_dt || '')}</b>
            </p>
          </div>
          {done.vs && (
            <div className="mt-6 bg-gradient-to-br from-steel-50 to-white border border-steel-200 p-4 text-center">
              <div className="text-[10px] uppercase tracking-[0.20em] font-bold text-steel-700">Variabilní symbol</div>
              <div className="mt-1 font-mono text-2xl font-bold text-ink-900 tracking-wider">{done.vs}</div>
            </div>
          )}
          <div className="mt-6 flex flex-col gap-2">
            <a href="#" className="btn-book w-full text-center">Hotovo</a>
            <a href="#pravidla" className="text-ink-700 text-[11px] uppercase tracking-[0.10em] font-semibold text-center hover:text-brand-500">
              Pravidla rezervace · věrnostka
            </a>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="bg-gradient-to-b from-white to-steel-50/40 py-12 md:py-16 min-h-screen">
      <div className="container-x max-w-xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-steel-800 via-steel-700 to-steel-600 text-white px-6 py-5 relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-brand-500 to-brand-700" />
          <div className="absolute right-0 -top-12 -mr-12 w-44 h-44 rounded-full bg-brand-500/15 blur-2xl" />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.30em] text-brand-300 font-bold flex items-center gap-2">
              <span className="inline-block w-5 h-px bg-brand-400" /> Přesun rezervace
            </div>
            <div className="text-lg font-bold tracking-tight mt-1">
              {info?.resource_label || `Rezervace #${reservationId}`}
            </div>
            {info && (
              <div className="text-[12px] text-brand-200 font-semibold mt-1">
                Původní termín: {fmtDateTime(info.from_dt)}
                {info.price_czk ? ` · ${info.price_czk.toLocaleString('cs')} Kč` : ''}
              </div>
            )}
            <p className="text-[12px] text-white/80 mt-2 leading-snug">
              {isStay ? 'Vyberte nový termín pobytu.' : 'Vyberte nový datum a čas.'}{' '}
              Stará rezervace se automaticky zruší.
              {info?.lead_text ? ` Přesunout lze nejpozději ${info.lead_text}.` : ''}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="bg-white border border-t-0 border-ink-900/10 p-5 md:p-6 shadow-[0_20px_60px_-20px_rgba(16,43,69,0.25)]">
          {/* Důvod, proč přesun nejde — řekneme to hned, ne až po výběru */}
          {(infoErr || (info && !info.can_reschedule)) && (
            <div className="mb-5 bg-brand-50 border border-brand-200 text-brand-700 text-[13px] px-3 py-2.5">
              {infoErr || info?.reason}
            </div>
          )}

          {/* Kalendář */}
          <div className="flex items-center justify-between mb-5">
            <button onClick={prevMonth} className="w-10 h-10 grid place-items-center hover:bg-brand-50 hover:border-brand-300 hover:text-brand-600 border border-ink-900/10 text-ink-900 transition-colors text-lg">‹</button>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.25em] text-brand-500 font-bold">Měsíc</div>
              <div className="font-bold uppercase tracking-wider text-ink-900 text-sm mt-0.5">
                {CZ_MONTHS[viewMonth]} <span className="text-ink-700/60 font-normal">{viewYear}</span>
              </div>
            </div>
            <button onClick={nextMonth} className="w-10 h-10 grid place-items-center hover:bg-brand-50 hover:border-brand-300 hover:text-brand-600 border border-ink-900/10 text-ink-900 transition-colors text-lg">›</button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-ink-700 font-semibold mb-2">
            {CZ_DAYS.map((d) => <div key={d}>{d}</div>)}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {days.map((d, i) => {
              if (!d) return <div key={i} />
              const sel = isStay && selDate && selTo
                ? d.iso >= selDate && d.iso <= selTo
                : d.iso === selDate
              const past = d.iso < new Date(Date.now() + 24*3600*1000).toISOString().slice(0,10)
              return (
                <button
                  key={i}
                  disabled={past}
                  onClick={() => {
                    if (!isStay) { setSelDate(d.iso); setSelHour(null); return }
                    // pobyt: 1. klik = příjezd, 2. klik = odjezd
                    if (!selDate || selTo || d.iso <= selDate) { setSelDate(d.iso); setSelTo(null) }
                    else setSelTo(d.iso)
                  }}
                  className={`aspect-square flex items-center justify-center text-sm transition border ${
                    past ? 'text-ink-700/30 border-transparent cursor-not-allowed' :
                    sel ? 'bg-brand-500 text-white border-brand-500 font-bold' :
                    'hover:bg-brand-50 border-ink-900/10 text-ink-800'
                  }`}
                >
                  {d.day}
                </button>
              )
            })}
          </div>

          {/* Hodiny — jen u sportu a sauny; pobyt se vybírá rozsahem dnů */}
          {selDate && !isStay && (
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.20em] font-bold text-brand-500 mb-2">Vyberte čas</div>
              {loading ? (
                <div className="text-sm text-ink-700">Načítám obsazení…</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {slots.map((h) => {
                    const taken = hourTaken(h)
                    const sel = selHour === h
                    return (
                      <button
                        key={h}
                        disabled={taken}
                        onClick={() => setSelHour(h)}
                        className={`px-2 py-2 text-sm font-bold transition border ${
                          taken ? 'border-ink-900/10 text-ink-700/30 cursor-not-allowed line-through' :
                          sel ? 'bg-brand-500 text-white border-brand-500' :
                          'hover:bg-brand-50 border-ink-900/15 text-ink-800'
                        }`}
                      >
                        {h}
                      </button>
                    )
                  })}
                </div>
              )}
              <p className="text-[11px] text-ink-700/70 mt-2">
                Hodinové sloty 9:00–21:00. Obsazené jsou škrtnuté.
              </p>
            </div>
          )}

          {isStay && (
            <div className="mt-5 text-sm text-ink-700">
              {!selDate && <>Vyberte nové datum příjezdu.</>}
              {selDate && !selTo && <>Příjezd: <b className="text-ink-900">{dayOnly(selDate)}</b> · klikněte na datum odjezdu</>}
              {selDate && selTo && <>Nový termín: <b className="text-ink-900">{dayOnly(selDate)} – {dayOnly(selTo)}</b></>}
            </div>
          )}

          {err && <div className="mt-4 text-brand-600 text-sm font-semibold">{err}</div>}

          <button
            disabled={busy || !selDate || (isStay ? !selTo : !selHour) || (!!info && !info.can_reschedule)}
            onClick={submit}
            className="btn-book w-full mt-6 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-brand-700/20"
          >
            {busy ? 'Přesouvám…' : 'Potvrdit přesun →'}
          </button>

          <p className="text-[11px] text-ink-700/70 mt-3 text-center leading-snug">
            Přesun je možný nejpozději 24 h před původním termínem a jen jednou.
            <br />
            Pro další změny volejte <a href="tel:+420775140705" className="text-brand-600 font-bold">775 140 705</a>.
          </p>
        </div>
      </div>
    </section>
  )
}

function fmtDateTime(iso: string) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('cs-CZ', {
    day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
  })
}

function buildMonth(year: number, month: number): ({ iso: string; day: number } | null)[] {
  const first = new Date(year, month, 1)
  const startWeekday = (first.getDay() + 6) % 7
  const cells: ({ iso: string; day: number } | null)[] = Array(startWeekday).fill(null)
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    cells.push({ iso, day: d })
  }
  while (cells.length % 7) cells.push(null)
  return cells
}

/** Jen datum — czDate() přidává i čas, což u pobytu dává nesmyslné „v 02:00". */
function dayOnly(iso: string) {
  if (!iso) return ''
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('cs-CZ', {
    day: 'numeric', month: 'long', year: 'numeric',
  })
}

function czDate(iso: string) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
